import { type ILogger } from '@cian/logger/shared';
import {
  GenericError,
  HttpBadStatusCodeError,
  type IErrorDescription,
  type IErrorDetails,
  UnknownError,
} from '@cian/peperrors/shared';

import { type ILogRequestService, LogRequestService } from '@/packages/errors';

import { type AssertSettings, type IFetchAssertsService } from '../IFetchAssertsService';

export class FetchAssertsService implements IFetchAssertsService {
  private readonly domain: string;
  private readonly model: object;
  private readonly logRequestService: ILogRequestService;

  public constructor(model: object, domain: string, logger: ILogger) {
    this.domain = domain;
    this.model = model;
    this.logRequestService = new LogRequestService(logger);
  }

  public assertUnknownServerError(
    error: unknown,
    { logLevel = 'error', errorMessage }: AssertSettings = {},
  ): UnknownError | GenericError {
    const defaultDetails: IErrorDetails = {
      level: logLevel,
      model: this.getSerializedModel(),
    };

    let validError: GenericError | UnknownError = new UnknownError({
      details: {
        ...defaultDetails,
        description: 'Не была передана ошибка, передалось неизвестно что, нужно разбираться',
      },
      domain: this.domain,
      message: errorMessage || 'Неизвестная ошибка в запросе',
    });

    if (GenericError.is(error)) {
      validError = new GenericError({
        ...(error.toJSON() as IErrorDescription),
        ...defaultDetails,
        ...error.details,
        message: error.message,
      });
    } else if (error instanceof Error) {
      validError = new UnknownError({
        details: {
          ...defaultDetails,
          description: 'При ошибке был передан инстанс Error, распарсили его и привели к UnknownError',
          name: error.name,
          // Если переопределяем сообщение об ошибке, то в details кладем исходное сообщ., чтобы не потерялось
          ...(errorMessage ? { message: error.message } : undefined),
        },
        domain: this.domain,
        message: errorMessage || error.message,
      });
    }

    this.logRequestService.logFetchError(validError);

    return validError;
  }

  public assertUnknownStatusCodeError(
    statusCode: number,
    { logLevel = 'error', errorMessage }: AssertSettings = {},
  ): HttpBadStatusCodeError {
    const httpBadStatusCodeError = new HttpBadStatusCodeError({
      details: {
        level: logLevel,
        model: this.getSerializedModel(),
      },
      domain: this.domain,
      message: errorMessage || 'Неизвестный статус ответа',
      statusCode,
    });

    this.logRequestService.logFetchError(httpBadStatusCodeError);

    return httpBadStatusCodeError;
  }

  public assert400StatusCodeError<Result extends { response?: { errors?: unknown[]; message?: string } }>(
    result: Result,
    { logLevel = 'warning', errorMessage }: AssertSettings = {},
  ): HttpBadStatusCodeError {
    const errors = JSON.stringify(
      result.response?.errors?.length ? result.response.errors : [{ message: 'Сервер не прислал список из ошибок' }],
    );
    const originalErrorMessage = result.response?.message || 'Неизвестная ошибка в запросе';

    const httpBadStatusCodeError = new HttpBadStatusCodeError({
      details: {
        errors,
        level: logLevel,
        model: this.getSerializedModel(),
        // Если переопределяем сообщение об ошибке, то в details кладем исходное сообщ., чтобы не потерялось
        ...(errorMessage ? { message: originalErrorMessage } : undefined),
      },
      domain: this.domain,
      message: errorMessage || originalErrorMessage,
      statusCode: 400,
    });

    this.logRequestService.logFetchError(httpBadStatusCodeError);

    return httpBadStatusCodeError;
  }

  private getSerializedModel(): string {
    return JSON.stringify(this.model);
  }
}
